import './TicketShowLogistic.scss';

import { useList } from '@refinedev/core';
import { Divider, Empty, Flex, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import LinkTextField from '@/components/general/LinkTextField';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { Ticket } from '@/model/Ticket/Ticket';
import { RESOURCES } from '@/Resources';

import TicketShowLogisticPackageCard from './TicketShowLogisticPackageCard';

interface TicketShowLogisticProps {
  record?: Ticket;
}

const TicketShowLogistic = (props: TicketShowLogisticProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { record } = props;
  const {
    result: shipmentsResult,
    query: { isLoading: isLoadingShipments },
  } = useList<Shipment>({
    resource: RESOURCES.SHIPMENTS,
    pagination: { pageSize: 1000 },
    filters: [{ field: 'ticket', operator: 'eq', value: record?.id }],
  });
  const {
    result: receptionsResult,
    query: { isLoading: isLoadingReceptions },
  } = useList<Reception>({
    resource: RESOURCES.RECEPTIONS,
    pagination: { pageSize: 1000 },
    filters: [{ field: 'ticket', operator: 'eq', value: record?.id }],
  });

  return (
    <div className="TicketShowLogistic">
      <div className="TicketShowLogisticSection">
        {(record?.has_receptions || record?.has_shipments) && (
          <Flex gap={4} style={{ marginBottom: 8 }}>
            <b>{t('Caso logística')}: </b>
            <LinkTextField
              label={`${t('Caso')} #${record?.id}`}
              to={`/${RESOURCES.CASE_RESOLUTION}/${record?.id}`}
              local
            />
          </Flex>
        )}
        <b>{t('Envíos')}</b>
        <div className="TicketShowLogisticCardsContainer">
          {isLoadingShipments ? (
            <Skeleton />
          ) : (
            <>
              {shipmentsResult?.data && shipmentsResult.data.length > 0 ? (
                <>
                  {shipmentsResult?.data?.map(shipment => (
                    <TicketShowLogisticPackageCard
                      key={shipment.id}
                      record={shipment}
                      type="shipment"
                    />
                  ))}
                </>
              ) : (
                <Empty description={t('No existen envíos')} />
              )}
            </>
          )}
        </div>
        {hasPermission('create', RESOURCES.SHIPMENTS) && (
          <Link
            className="TicketShowLogisticLink"
            to={`/shipments/create?ticket=${record?.id}&company=${record?.empresa}`}>
            {t('Crear nuevo envío')}
          </Link>
        )}
      </div>
      <Divider />
      <div className="TicketShowLogisticSection">
        <b>{t('Recepciones')}</b>
        <div className="TicketShowLogisticCardsContainer">
          {isLoadingReceptions ? (
            <Skeleton />
          ) : (
            <>
              {receptionsResult?.data && receptionsResult.data.length > 0 ? (
                <>
                  {receptionsResult?.data?.map(reception => (
                    <TicketShowLogisticPackageCard
                      key={reception.id}
                      record={reception}
                      type="reception"
                    />
                  ))}
                </>
              ) : (
                <Empty description={t('No existen recepciones')} />
              )}
            </>
          )}
        </div>
        {hasPermission('create', RESOURCES.RECEPTIONS) && (
          <Link className="TicketShowLogisticLink" to={`/receptions/create?ticket=${record?.id}`}>
            {t('Crear nueva recepción')}
          </Link>
        )}
      </div>
    </div>
  );
};

export default TicketShowLogistic;
